import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SubmitArticle.module.css";
import { articleAPI } from "../../api/article";
import { affiliationsAPI } from "../../api/affiliations";
import { useContent } from "../../hooks/useContent";
import { articleSubmitEn } from "../../content/articleSubmitEn";

const SubmitArticle = () => {
  const navigate = useNavigate();
  const { t } = useContent();
  const d = articleSubmitEn;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [affiliations, setAffiliations] = useState([]);

  // Выбранный язык
  const [selectedLanguage, setSelectedLanguage] = useState("en");

  // Данные формы
  const [formData, setFormData] = useState({
    // Шаг 1: Информация о статье
    translations: {
      en: { title: "", annotation: "", content: "" },
      ru: { title: "", annotation: "", content: "" },
      uz: { title: "", annotation: "", content: "" },
    },
    keywords: "",
    section: "",
    submission_type: "JOURNAL_ONLY",
    page_count: 0,
    is_featured: false,

    // Шаг 2: Информация об авторах
    authors: [
      {
        first_name: "",
        last_name: "",
        email: "",
        orcid: "",
        country: "",
        affiliation: "",
        department: "",
        is_corresponding: true,
      },
    ],

    // Шаг 3: Загрузка файлов
    manuscriptFile: null,
    coverLetterFile: null,
  });

  // Загрузка affiliations
  useEffect(() => {
    const fetchAffiliations = async () => {
      try {
        const data = await affiliationsAPI.getAll();
        setAffiliations(data);
      } catch (err) {
        console.error("Failed to load affiliations:", err);
      }
    };
    fetchAffiliations();
  }, []);

  // Обработка переводов
  const handleTranslation = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value,
        },
      },
    }));
  };

  // Обработка авторов
  const handleAuthorChange = (index, field, value) => {
    const newAuthors = [...formData.authors];
    newAuthors[index][field] = value;
    setFormData({ ...formData, authors: newAuthors });
  };

  const addAuthor = () => {
    setFormData({
      ...formData,
      authors: [
        ...formData.authors,
        {
          first_name: "",
          last_name: "",
          email: "",
          orcid: "",
          country: "",
          affiliation: "",
          department: "",
          is_corresponding: false,
        },
      ],
    });
  };

  const removeAuthor = (index) => {
    if (formData.authors.length > 1) {
      const newAuthors = formData.authors.filter((_, i) => i !== index);
      setFormData({ ...formData, authors: newAuthors });
    }
  };

  // Обработка файлов
  const handleFileChange = (field, file) => {
    if (file && file.type === "application/pdf") {
      setFormData({ ...formData, [field]: file });
      setErrorMsg("");
    } else {
      setErrorMsg("Please upload a PDF file");
    }
  };

  // Валидация шагов
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.translations.en.title.trim()) {
          setErrorMsg(t("articleSubmit.errors.enTitleRequired", d.errors.enTitleRequired));
          return false;
        }
        if (!formData.translations.en.annotation.trim()) {
          setErrorMsg(t("articleSubmit.errors.enAnnotationRequired", d.errors.enAnnotationRequired));
          return false;
        }
        if (!formData.keywords.trim()) {
          setErrorMsg(t("articleSubmit.errors.keywordsRequired", d.errors.keywordsRequired));
          return false;
        }
        if (!formData.section) {
          setErrorMsg(t("articleSubmit.errors.sectionRequired", d.errors.sectionRequired));
          return false;
        }
        break;

      case 2:
        for (let i = 0; i < formData.authors.length; i++) {
          const author = formData.authors[i];
          if (!author.first_name.trim() || !author.last_name.trim()) {
            setErrorMsg(
              t("articleSubmit.errors.authorFirstLast", d.errors.authorFirstLast).replace(
                "{num}",
                String(i + 1),
              ),
            );
            return false;
          }
          if (!author.email.trim()) {
            setErrorMsg(
              t("articleSubmit.errors.authorEmail", d.errors.authorEmail).replace("{num}", String(i + 1)),
            );
            return false;
          }
          if (!author.affiliation) {
            setErrorMsg(
              t("articleSubmit.errors.authorAffiliation", d.errors.authorAffiliation).replace(
                "{num}",
                String(i + 1),
              ),
            );
            return false;
          }
        }
        const hasCorresponding = formData.authors.some(
          (a) => a.is_corresponding
        );
        if (!hasCorresponding) {
          setErrorMsg(t("articleSubmit.errors.correspondingRequired", d.errors.correspondingRequired));
          return false;
        }
        break;

      case 3:
        if (!formData.manuscriptFile) {
          setErrorMsg(t("articleSubmit.errors.manuscriptRequired", d.errors.manuscriptRequired));
          return false;
        }
        break;

      default:
        break;
    }

    setErrorMsg("");
    return true;
  };

  // Навигация по шагам
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setErrorMsg("");
  };

  // Отправка формы
  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setLoading(true);
    setErrorMsg("");

    try {
      // 1. Создаем статью
      const articleData = {
        translations: formData.translations,
        submission_type: formData.submission_type,
        page_count: formData.page_count,
        is_featured: formData.is_featured,
      };

      const articleResponse = await articleAPI.create(articleData);
      const articleId = articleResponse.id;

      // 2. Загружаем файл рукописи
      if (formData.manuscriptFile) {
        await articleAPI.uploadFile(
          formData.manuscriptFile,
          "ARTICLE",
          articleId,
          1
        );
      }

      // 3. Загружаем сопроводительное письмо (если есть)
      if (formData.coverLetterFile) {
        await articleAPI.uploadFile(
          formData.coverLetterFile,
          "COVER_LETTER",
          articleId,
          1
        );
      }

      // Успех!
      setCurrentStep(4);
    } catch (err) {
      console.error("Submission error:", err);
      setErrorMsg(err?.message || t("articleSubmit.errors.submitFailed", d.errors.submitFailed));
    } finally {
      setLoading(false);
    }
  };

  // Рендер шагов
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>{t("articleSubmit.step1.title", d.step1.title)}</h2>

            <div className={styles.langBlock}>
              <h3>{t("articleSubmit.step1.enBlockTitle", d.step1.enBlockTitle)}</h3>

              <label className={styles.label}>
                {t("articleSubmit.step1.labelEnTitle", d.step1.labelEnTitle)}{" "}
                <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.input}
                value={formData.translations.en.title}
                onChange={(e) =>
                  handleTranslation("en", "title", e.target.value)
                }
                placeholder={t("articleSubmit.step1.phEnTitle", d.step1.phEnTitle)}
              />

              <label className={styles.label}>
                {t("articleSubmit.step1.labelEnAbstract", d.step1.labelEnAbstract)}{" "}
                <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.textarea}
                value={formData.translations.en.annotation}
                onChange={(e) =>
                  handleTranslation("en", "annotation", e.target.value)
                }
                placeholder={t("articleSubmit.step1.phEnAbstract", d.step1.phEnAbstract)}
                rows="6"
              />

              <label className={styles.label}>{t("articleSubmit.step1.labelEnKeywordsField", d.step1.labelEnKeywordsField)}</label>
              <textarea
                className={styles.textarea}
                value={formData.translations.en.content}
                onChange={(e) =>
                  handleTranslation("en", "content", e.target.value)
                }
                placeholder={t("articleSubmit.step1.phEnKeywordsField", d.step1.phEnKeywordsField)}
                rows="4"
              />
            </div>

            <div className={styles.langBlock}>
              <h3>{t("articleSubmit.step1.ruBlockTitle", d.step1.ruBlockTitle)}</h3>

              <label className={styles.label}>{t("articleSubmit.step1.labelRuTitle", d.step1.labelRuTitle)}</label>
              <input
                type="text"
                className={styles.input}
                value={formData.translations.ru.title}
                onChange={(e) =>
                  handleTranslation("ru", "title", e.target.value)
                }
                placeholder={t("articleSubmit.step1.phRuTitle", d.step1.phRuTitle)}
              />

              <label className={styles.label}>{t("articleSubmit.step1.labelRuAbstract", d.step1.labelRuAbstract)}</label>
              <textarea
                className={styles.textarea}
                value={formData.translations.ru.annotation}
                onChange={(e) =>
                  handleTranslation("ru", "annotation", e.target.value)
                }
                placeholder={t("articleSubmit.step1.phRuAbstract", d.step1.phRuAbstract)}
                rows="6"
              />
            </div>

            <div className={styles.langBlock}>
              <h3>{t("articleSubmit.step1.uzBlockTitle", d.step1.uzBlockTitle)}</h3>

              <label className={styles.label}>{t("articleSubmit.step1.labelUzTitle", d.step1.labelUzTitle)}</label>
              <input
                type="text"
                className={styles.input}
                value={formData.translations.uz.title}
                onChange={(e) =>
                  handleTranslation("uz", "title", e.target.value)
                }
                placeholder={t("articleSubmit.step1.phUzTitle", d.step1.phUzTitle)}
              />

              <label className={styles.label}>{t("articleSubmit.step1.labelUzAbstract", d.step1.labelUzAbstract)}</label>
              <textarea
                className={styles.textarea}
                value={formData.translations.uz.annotation}
                onChange={(e) =>
                  handleTranslation("uz", "annotation", e.target.value)
                }
                placeholder={t("articleSubmit.step1.phUzAbstract", d.step1.phUzAbstract)}
                rows="6"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  {t("articleSubmit.step1.keywordsLabel", d.step1.keywordsLabel)}{" "}
                  <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.keywords}
                  onChange={(e) =>
                    setFormData({ ...formData, keywords: e.target.value })
                  }
                  placeholder={t("articleSubmit.step1.keywordsPlaceholder", d.step1.keywordsPlaceholder)}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  {t("articleSubmit.step1.sectionLabel", d.step1.sectionLabel)}{" "}
                  <span className={styles.required}>*</span>
                </label>
                <select
                  className={styles.input}
                  value={formData.section}
                  onChange={(e) =>
                    setFormData({ ...formData, section: e.target.value })
                  }
                >
                  <option value="">{t("articleSubmit.step1.sectionPlaceholder", d.step1.sectionPlaceholder)}</option>
                  <option value="computer_science">
                    {t("articleSubmit.step1.sectionComputerScience", d.step1.sectionComputerScience)}
                  </option>
                  <option value="engineering">{t("articleSubmit.step1.sectionEngineering", d.step1.sectionEngineering)}</option>
                  <option value="mathematics">{t("articleSubmit.step1.sectionMathematics", d.step1.sectionMathematics)}</option>
                  <option value="physics">{t("articleSubmit.step1.sectionPhysics", d.step1.sectionPhysics)}</option>
                  <option value="other">{t("articleSubmit.step1.sectionOther", d.step1.sectionOther)}</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("articleSubmit.step1.submissionTypeLabel", d.step1.submissionTypeLabel)}</label>
                <select
                  className={styles.input}
                  value={formData.submission_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      submission_type: e.target.value,
                    })
                  }
                >
                  <option value="JOURNAL_ONLY">
                    {t("articleSubmit.step1.submissionJournalOnly", d.step1.submissionJournalOnly)}
                  </option>
                  <option value="CONFERENCE_ONLY">
                    {t("articleSubmit.step1.submissionConferenceOnly", d.step1.submissionConferenceOnly)}
                  </option>
                  <option value="BOTH">{t("articleSubmit.step1.submissionBoth", d.step1.submissionBoth)}</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{t("articleSubmit.step1.pageCountLabel", d.step1.pageCountLabel)}</label>
                <input
                  type="number"
                  className={styles.input}
                  min="1"
                  value={formData.page_count}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      page_count: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder={t("articleSubmit.step1.pageCountPlaceholder", d.step1.pageCountPlaceholder)}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>{t("articleSubmit.step2.title", d.step2.title)}</h2>

            {formData.authors.map((author, index) => (
              <div key={index} className={styles.authorBlock}>
                <div className={styles.authorHeader}>
                  <h3>
                    {t("articleSubmit.step2.authorHeading", d.step2.authorHeading).replace("{num}", String(index + 1))}
                  </h3>
                  {formData.authors.length > 1 && (
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => removeAuthor(index)}
                    >
                      {t("articleSubmit.buttons.remove", d.buttons.remove)}
                    </button>
                  )}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      {t("articleSubmit.step2.labelFirstName", d.step2.labelFirstName)}{" "}
                      <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      className={styles.input}
                      value={author.first_name}
                      onChange={(e) =>
                        handleAuthorChange(index, "first_name", e.target.value)
                      }
                      placeholder={t("articleSubmit.step2.phFirstName", d.step2.phFirstName)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      {t("articleSubmit.step2.labelLastName", d.step2.labelLastName)}{" "}
                      <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      className={styles.input}
                      value={author.last_name}
                      onChange={(e) =>
                        handleAuthorChange(index, "last_name", e.target.value)
                      }
                      placeholder={t("articleSubmit.step2.phLastName", d.step2.phLastName)}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      {t("articleSubmit.step2.labelEmail", d.step2.labelEmail)}{" "}
                      <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="email"
                      className={styles.input}
                      value={author.email}
                      onChange={(e) =>
                        handleAuthorChange(index, "email", e.target.value)
                      }
                      placeholder={t("articleSubmit.step2.phEmail", d.step2.phEmail)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("articleSubmit.step2.labelOrcid", d.step2.labelOrcid)}</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={author.orcid}
                      onChange={(e) =>
                        handleAuthorChange(index, "orcid", e.target.value)
                      }
                      placeholder={t("articleSubmit.step2.phOrcid", d.step2.phOrcid)}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      {t("articleSubmit.step2.labelCountry", d.step2.labelCountry)}{" "}
                      <span className={styles.required}>*</span>
                    </label>
                    <select
                      className={styles.input}
                      value={author.country}
                      onChange={(e) =>
                        handleAuthorChange(index, "country", e.target.value)
                      }
                    >
                      <option value="">{t("articleSubmit.step2.countryPlaceholder", d.step2.countryPlaceholder)}</option>
                      <option value="Uzbekistan">{t("articleSubmit.step2.countryUzbekistan", d.step2.countryUzbekistan)}</option>
                      <option value="Russia">{t("articleSubmit.step2.countryRussia", d.step2.countryRussia)}</option>
                      <option value="Kazakhstan">{t("articleSubmit.step2.countryKazakhstan", d.step2.countryKazakhstan)}</option>
                      <option value="USA">{t("articleSubmit.step2.countryUSA", d.step2.countryUSA)}</option>
                      <option value="Germany">{t("articleSubmit.step2.countryGermany", d.step2.countryGermany)}</option>
                      <option value="Other">{t("articleSubmit.step2.countryOther", d.step2.countryOther)}</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      {t("articleSubmit.step2.labelAffiliation", d.step2.labelAffiliation)}{" "}
                      <span className={styles.required}>*</span>
                    </label>
                    <select
                      className={styles.input}
                      value={author.affiliation}
                      onChange={(e) =>
                        handleAuthorChange(
                          index,
                          "affiliation",
                          e.target.value
                        )
                      }
                    >
                      <option value="">{t("articleSubmit.step2.affiliationPlaceholder", d.step2.affiliationPlaceholder)}</option>
                      {affiliations.map((aff) => (
                        <option key={aff.id} value={aff.name}>
                          {aff.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>{t("articleSubmit.step2.labelDepartment", d.step2.labelDepartment)}</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={author.department}
                    onChange={(e) =>
                      handleAuthorChange(index, "department", e.target.value)
                    }
                    placeholder={t("articleSubmit.step2.phDepartment", d.step2.phDepartment)}
                  />
                </div>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={author.is_corresponding}
                    onChange={(e) =>
                      handleAuthorChange(
                        index,
                        "is_corresponding",
                        e.target.checked
                      )
                    }
                  />
                  {t("articleSubmit.step2.correspondingAuthor", d.step2.correspondingAuthor)}
                </label>
              </div>
            ))}

            <button
              type="button"
              className={styles.addAuthorBtn}
              onClick={addAuthor}
            >
              {t("articleSubmit.buttons.addAuthor", d.buttons.addAuthor)}
            </button>
          </div>
        );

      case 3:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>{t("articleSubmit.step3.title", d.step3.title)}</h2>

            <div className={styles.uploadSection}>
              <h3>
                {t("articleSubmit.step3.manuscriptTitle", d.step3.manuscriptTitle)}{" "}
                <span className={styles.required}>*</span>
              </h3>
              <p className={styles.uploadHint}>{t("articleSubmit.step3.manuscriptHint", d.step3.manuscriptHint)}</p>

              <label className={styles.fileLabel}>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileChange("manuscriptFile", e.target.files[0])
                  }
                  className={styles.fileInput}
                />
                <div className={styles.fileButton}>
                  {formData.manuscriptFile
                    ? formData.manuscriptFile.name
                    : t("articleSubmit.buttons.choosePdf", d.buttons.choosePdf)}
                </div>
              </label>
            </div>

            <div className={styles.uploadSection}>
              <h3>{t("articleSubmit.step3.coverTitle", d.step3.coverTitle)}</h3>
              <p className={styles.uploadHint}>{t("articleSubmit.step3.coverHint", d.step3.coverHint)}</p>

              <label className={styles.fileLabel}>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileChange("coverLetterFile", e.target.files[0])
                  }
                  className={styles.fileInput}
                />
                <div className={styles.fileButton}>
                  {formData.coverLetterFile
                    ? formData.coverLetterFile.name
                    : t("articleSubmit.buttons.choosePdf", d.buttons.choosePdf)}
                </div>
              </label>
            </div>

            <div className={styles.infoBox}>
              <h4>{t("articleSubmit.step3.guidelinesTitle", d.step3.guidelinesTitle)}</h4>
              <ul>
                <li>{t("articleSubmit.step3.g1", d.step3.g1)}</li>
                <li>{t("articleSubmit.step3.g2", d.step3.g2)}</li>
                <li>{t("articleSubmit.step3.g3", d.step3.g3)}</li>
                <li>{t("articleSubmit.step3.g4", d.step3.g4)}</li>
                <li>{t("articleSubmit.step3.g5", d.step3.g5)}</li>
              </ul>
            </div>
          </div>
        );

      case 4:
        return (
          <div className={styles.stepContent}>
            <div className={styles.successBox}>
              <div className={styles.successIcon}>✓</div>
              <h2>{t("articleSubmit.step4.successTitle", d.step4.successTitle)}</h2>
              <p>{t("articleSubmit.step4.successP", d.step4.successP)}</p>
              <p>
                <strong>{t("articleSubmit.step4.whatsNext", d.step4.whatsNext)}</strong>
              </p>
              <ul className={styles.nextSteps}>
                <li>{t("articleSubmit.step4.next1", d.step4.next1)}</li>
                <li>{t("articleSubmit.step4.next2", d.step4.next2)}</li>
                <li>{t("articleSubmit.step4.next3", d.step4.next3)}</li>
                <li>{t("articleSubmit.step4.next4", d.step4.next4)}</li>
              </ul>
              <button
                className={styles.primaryBtn}
                onClick={() => navigate("/dashboard")}
              >
                {t("articleSubmit.buttons.dashboard", d.buttons.dashboard)}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {/* Progress Steps */}
        <div className={styles.progressBar}>
          {[
            { num: 1, label: t("articleSubmit.progress.step1", d.progress.step1) },
            { num: 2, label: t("articleSubmit.progress.step2", d.progress.step2) },
            { num: 3, label: t("articleSubmit.progress.step3", d.progress.step3) },
            { num: 4, label: t("articleSubmit.progress.step4", d.progress.step4) },
          ].map((step) => (
            <div
              key={step.num}
              className={`${styles.step} ${
                currentStep >= step.num ? styles.activeStep : ""
              }`}
            >
              <div className={styles.stepNumber}>{step.num}</div>
              <div className={styles.stepLabel}>{step.label}</div>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {errorMsg && <div className={styles.errorBox}>{errorMsg}</div>}

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        {currentStep < 4 && (
          <div className={styles.buttonGroup}>
            {currentStep > 1 && (
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={prevStep}
                disabled={loading}
              >
                {t("articleSubmit.buttons.previous", d.buttons.previous)}
              </button>
            )}

            {currentStep < 3 ? (
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={nextStep}
                disabled={loading}
              >
                {t("articleSubmit.buttons.next", d.buttons.next)}
              </button>
            ) : (
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading
                  ? t("articleSubmit.buttons.submitting", d.buttons.submitting)
                  : t("articleSubmit.buttons.submit", d.buttons.submit)}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitArticle;